<template>
  <div class="mobile-city" :class="{ open: isOpen }">
    <div class="mobile-city__row">
      <!-- Most visitors reach for the picker to get back to their own city's
           service page, so that page is a link of its own here and the picker
           keeps only the swap. -->
      <a
        class="mobile-city__service"
        v-if="service"
        :href="service.url"
        @click="reportServiceClick"
      >
        <span class="mobile-city__service-text">
          <span class="mobile-city__service-brand">{{ service.brand }}</span>
          <span class="mobile-city__service-city">{{ service.city }}</span>
        </span>

        <svg aria-hidden="true" class="icon mobile-city__service-chevron">
          <use href="#chevron-right" />
        </svg>
      </a>

      <button
        aria-controls="mobile-city-panel"
        class="mobile-city__toggle"
        type="button"
        :aria-expanded="isOpen"
        :aria-label="toggleLabel"
        :class="{ 'mobile-city__toggle--compact': hasService }"
        @click="toggle"
      >
        <svg aria-hidden="true" class="icon"><use :href="toggleIcon" /></svg>

        <span v-text="toggleText" />

        <svg
          aria-hidden="true"
          class="icon mobile-city__chevron"
          v-if="!hasService"
        >
          <use href="#chevron-down" />
        </svg>
      </button>
    </div>

    <transition
      name="mobile-menu-sheet-up"
      @after-enter="isEntering = false"
      @before-enter="isEntering = true"
    >
      <div
        class="mobile-city__panel"
        id="mobile-city-panel"
        v-show="isOpen"
        :class="{ 'is-entering': isEntering }"
      >
        <CityPanel
          layout="sheet"
          ref="panel"
          :city-changed="internallySelected"
          :city-term="cityTerm"
          @select="setSelectedTerm"
        />
      </div>
    </transition>
  </div>
</template>

<script>
  import CityPanel from "Components/CityPanel.vue";
  import { useCityTermLocalStorage } from "Main/composables/useCityTermLocalStorage";
  import { useRoleTermLocalStorage } from "Main/composables/useRoleTermLocalStorage";
  import { buildCityServices, pickPreferredService } from "Utils/city-services";
  import {
    pushCitySelectOpen,
    pushCityServiceClick,
    pushNewCitySelected,
  } from "Utils/city-tracking";
  import { mapState } from "vuex";

  /** Clears the sheet's scale, so the input is at full height when focused. */
  const FOCUS_DELAY = 200;

  export default {
    components: {
      CityPanel,
    },

    computed: {
      ...mapState("cities", ["cityLinks", "isLoadingCityLinks"]),
      ...mapState("mobileMenu", ["isMenuActive"]),

      hasService() {
        return !!this.service;
      },

      selected() {
        if (!this.cityTerm) {
          return null;
        }

        return {
          choice: this.cityTerm.id,
          label: this.cityTerm.name,
          slug: this.cityTerm.slug,
        };
      },

      /**
       * The one service the visitor is likeliest to want in the city they
       * picked — the same one the panel leads with before the rest are asked
       * for. Null leaves the picker as the footer's only action.
       */
      service() {
        if (!this.cityTerm) {
          return null;
        }

        return pickPreferredService(
          buildCityServices(
            this.cityLinks,
            this.texts,
            this.registeredPostTypes
          ),
          this.roleTerm
        );
      },

      /** Swapping is all that is left once the link carries the city. */
      toggleIcon() {
        return this.hasService ? "#arrow-right-arrow-left" : "#location-dot";
      },

      /**
       * The pill shows which city is picked, so what tapping it does is left to
       * the accessible name. Beside the link the pill says "change" instead,
       * and WCAG 2.5.3 wants that word inside the name — so the name spells out
       * what is changed rather than describing the panel, and holds still while
       * the panel opens, since the word on the button does too.
       */
      toggleLabel() {
        if (this.hasService) {
          return this.texts.changeCityLabel;
        }

        if (!this.selected) {
          return this.texts.chooseYourCity;
        }

        return this.isOpen ? this.texts.hideServices : this.texts.showServices;
      },

      toggleText() {
        if (this.hasService) {
          return this.texts.changeCity;
        }

        return this.selected ? this.selected.label : this.texts.chooseCity;
      },
    },

    data() {
      return {
        internallySelected: false,
        // Holds the content back until the sheet has finished growing.
        isEntering: false,
        isOpen: false,
        registeredPostTypes: theme_vars?.localConfig?.registeredPostTypes ?? [],
        texts: theme_vars.cityCaseTypeSelect,
      };
    },

    emits: ["open-panel"],

    methods: {
      close() {
        this.isOpen = false;
        this.$emit("open-panel", false);
      },

      getCityLinks() {
        if (!this.cityTerm) {
          return;
        }

        this.$store.dispatch("cities/getCityLink", this.cityTerm.id);
      },

      open() {
        if (this.cityTerm && !this.cityLinks) {
          this.getCityLinks();
        }

        this.isOpen = true;
        this.$emit("open-panel", true);
        pushCitySelectOpen("mobileMenu");

        if (this.selected) {
          return;
        }

        setTimeout(() => this.$refs.panel?.focusSearch(), FOCUS_DELAY);
      },

      // The shortcut beside the picker, not the panel inside it, so it reports
      // under its own name.
      reportServiceClick() {
        pushCityServiceClick(
          "mobileMenuShortcut",
          this.service,
          this.internallySelected
        );
      },

      setSelectedTerm(value) {
        this.internallySelected = true;
        this.cityTerm = value
          ? { id: value.choice, name: value.label, slug: value.slug }
          : null;

        this.saveCityTermToLocalStorage();
        this.getCityLinks();
      },

      toggle() {
        if (this.isOpen) {
          this.close();
          return;
        }

        this.open();
      },
    },
    // The panel used to be the only thing that needed the links, so it fetched
    // them on open. The footer link needs them before that, while the drawer is
    // still shut.
    mounted() {
      if (this.cityLinks || this.isLoadingCityLinks) {
        return;
      }

      this.getCityLinks();
    },
    name: "MobileCitySelect",

    setup() {
      const {
        cityTerm,
        saveCityTermToLocalStorage,
        getCityTermFromLocalStorage,
      } = useCityTermLocalStorage();
      const { roleTerm } = useRoleTermLocalStorage();

      return {
        cityTerm,
        getCityTermFromLocalStorage,
        roleTerm,
        saveCityTermToLocalStorage,
      };
    },

    watch: {
      // The top bar keeps its own copy of the city term, so a pick made there
      // only reaches this component through the shared store. Re-read on every
      // link refresh to stay in step with it.
      cityLinks() {
        this.cityTerm = this.getCityTermFromLocalStorage();
      },

      // Closing the drawer has to collapse the sheet too, or it folds back out
      // already open next time — and leaves the menu below it inert.
      isMenuActive(isActive) {
        if (!isActive) {
          this.close();
        }
      },
      selected(newValue, oldValue) {
        // newValue is null whenever the city is cleared — a pick reset, or a
        // re-read of local storage that no longer holds one.
        if (
          !(this.internallySelected && oldValue && newValue) ||
          newValue.label === oldValue.label
        ) {
          return;
        }

        pushNewCitySelected("mobileMenu", newValue.label, oldValue.label);
      },
    },
  };
</script>
